// core/services/authService.ts — async auth/session service (B3: API-backed session).
//
// B3 flip: signIn/signUp/signOut/loadSession call the Django session-cookie API
// (apiClient). The in-memory structures (a single active Account + the users registry)
// are kept so consumers (useSession, prefsService, components) are unchanged:
//   applySession(user) translates an API user into both, after each auth call.
//
// loadAdminSeed / hydrateAccountsIntoUsersRegistry remain functional (used by tests and
// available for offline seeding) but are NO LONGER in the production boot path — main.tsx
// calls loadSession() instead (login is real; WayneTech lives in the DB from the B2a seed).
//
// Still client-side until B4: updateProfile / updatePassword / isPremium /
// deleteAccount / downloadData (they read/write the cached account; the real profile/
// account-write API lands in B4).
//
// 1.23.00: setPremium (POST /users/me/premium/) is GONE — that route was
// deleted server-side (the free-upgrade hole; see apps.billing). `premium`
// is now purely server-derived; core/services/billingService.ts is the real
// (Stripe Checkout/Portal-backed) replacement.
//
// Reactivity: mutations update sessionStore (Zustand). Components use hooks/useSession.ts.

import type { Account, User, ProfileOverrides } from '@/core/models';
import { useSessionStore } from '@/core/stores/sessionStore';
import { useFollowStore } from '@/core/stores/followStore';
import { useForYouStore } from '@/core/stores/forYouStore';
import { useFeedStore } from '@/core/stores/feedStore';
import { usePostStore } from '@/core/stores/postStore';
import { useNotificationStore } from '@/core/stores/notificationStore';
import { useMessageStore } from '@/core/stores/messageStore';
import { useBookmarkStore } from '@/core/stores/bookmarkStore';
import { useMuteStore } from '@/core/stores/muteStore';
import { useCommunityStore } from '@/core/stores/communityStore';
import { useHydrationStore } from '@/core/stores/hydrationStore';
import { adminSeed } from '@/core/data/adminSeed';
import { getUser, registerUser } from '@/core/data/users';
import { usersService } from '@/core/services/usersService';
import { clearOnboardingDeferral } from '@/core/lib/onboardingDeferral';
import { clearReplyDrafts } from '@/core/lib/replyDraftStorage';
import { api, csrf, ApiError } from '@/core/lib/apiClient';
import { track } from '@/core/lib/analytics';

// ─── Errors ──────────────────────────────────────────────────────────────────

export class InvalidCredentials extends Error {
  constructor() {
    super('InvalidCredentials');
    this.name = 'InvalidCredentials';
  }
}

export class UsernameTaken extends Error {
  constructor() {
    super('UsernameTaken');
    this.name = 'UsernameTaken';
  }
}

export class RateLimited extends Error {
  constructor() {
    super('RateLimited');
    this.name = 'RateLimited';
  }
}

export class AccountSanctioned extends Error {
  /** 'suspended' or 'banned' */
  readonly sanctionStatus: 'suspended' | 'banned';
  readonly reason: string;
  readonly category: string;
  constructor(sanctionStatus: 'suspended' | 'banned', reason: string, category: string) {
    super('AccountSanctioned');
    this.name = 'AccountSanctioned';
    this.sanctionStatus = sanctionStatus;
    this.reason = reason;
    this.category = category;
  }
}

/**
 * Thrown by signIn when the credentials were CORRECT but the account has
 * confirmed 2FA — /auth/login/ answers with 200 {code:'totp_required',
 * challenge} rather than an error, since nothing actually failed yet. The
 * challenge is short-lived signed server state; hold it in component state
 * only (never persisted) and pass it to completeTotpSignIn.
 */
export class TotpRequired extends Error {
  readonly challenge: string;
  constructor(challenge: string) {
    super('TotpRequired');
    this.name = 'TotpRequired';
    this.challenge = challenge;
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function getAccounts(): Account[] {
  return useSessionStore.getState().accounts;
}

function setAccounts(list: Account[]): void {
  useSessionStore.getState().setAccounts(list);
}

/** Build an empty User registry record from an Account (mirrors legacy buildEmptyUserRecord). */
function buildEmptyUserRecord(acc: Account): User {
  const joinedMonth = new Date(acc.joinedAt).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const initial = (acc.displayName?.[0] ?? acc.username?.[0] ?? '?').toUpperCase();
  return {
    id: acc.userId,
    name: acc.displayName || acc.username,
    handle: acc.handle || '@' + acc.username,
    verified: false,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    status: 'active',
    avatarInitial: initial,
    avatarKey: 'av-grad',
    banner: 'cyberpunk-x',
    bio: '',
    tags: [],
    link: null,
    joined: joinedMonth,
    role: '',
    followerCount: 0,
    stats: { following: 0, followers: 0, posts: 0, likes: 0 },
    about: [{ icon: 'calendar', text: 'Joined ' + joinedMonth }],
    achievements: [],
    statsOverview: { headline: [], spark: [] },
    likedPostIds: [],
    likesPublic: false,
    emailVerified: false,
  };
}

// Monotonic counter for updateProfile calls — see the stale-response guard there.
let profileUpdateSeq = 0;

/** Populate the in-memory session structures from an API `/me` user. */
function applySession(user: User): void {
  // Keep this account's interrupted drafts, discard any previous account's.
  clearReplyDrafts(user.id);
  registerUser(user); // upsert into the users registry (getUser(user.id) → this record)
  const acc: Account = {
    userId: user.id,
    username: user.handle.replace(/^@/, ''),
    email: null,
    displayName: user.name,
    handle: user.handle,
    password: '', // session lives in the httpOnly cookie now, never client-side
    joinedAt: new Date().toISOString(),
    premium: user.premium,
    useThisAsActive: true,
  };
  setAccounts([acc]);
}

// ─── SignUp fields ────────────────────────────────────────────────────────────

/** Fields accepted by signUp. Mirrors the plan's test call:
 *  `{ handle: '@neo', displayName: 'Neo', password: 'pw' }`
 *  Also supports legacy-style `{ fullName, username, email, password }`. */
export interface SignUpFields {
  handle?: string;
  username?: string;
  displayName?: string;
  fullName?: string;
  email?: string;
  password: string;
  /**
   * 1.24.00 invites: the code from a `/join/<code>` link, when the sign-up
   * came through one. Optional and NEVER blocking — an unknown or revoked
   * code still registers; the outcome comes back in SignUpResult.invite.
   */
  inviteCode?: string;
}

/**
 * What signUp resolves to. `id` is the new account's id (there is still NO
 * auto-login). `invite` is the server's verdict on the invite code:
 * 'applied' (inviter credited), 'invalid' (unknown/revoked — the account was
 * created anyway and the form says so in one line), or null (no code sent).
 */
export interface SignUpResult {
  id: string;
  invite: 'applied' | 'invalid' | null;
}

// ─── authService ─────────────────────────────────────────────────────────────

export const authService = {
  /**
   * Idempotently seed the admin account into the in-memory store (offline seed).
   * NOT called in the production boot path anymore (login is real) — kept for tests
   * and offline registry seeding. Mirror of legacy loadAdminSeed.
   */
  loadAdminSeed(): void {
    const existing = getAccounts();
    if (existing.some((a) => a.userId === adminSeed.userId)) return;
    setAccounts([...existing, { ...adminSeed, useThisAsActive: false }]);
  },

  /** Find an account by username, handle, or email (case-insensitive username/handle match). */
  findAccount(needle: string): Account | undefined {
    const n = needle.trim();
    return getAccounts().find(
      (a) =>
        a.username === n ||
        a.handle === n ||
        a.handle?.replace(/^@/, '').toLowerCase() === n.replace(/^@/, '').toLowerCase() ||
        (a.email != null && a.email === n),
    );
  },

  /** Returns the active User record (from the users registry), or null. */
  getCurrentUser(): User | null {
    const active = getAccounts().find((a) => a.useThisAsActive === true);
    if (!active) return null;
    return getUser(active.userId);
  },

  /** Returns true when there is an active signed-in account. */
  isAuthenticated(): boolean {
    return getAccounts().some((a) => a.useThisAsActive === true);
  },

  /**
   * Sign in with a username, handle, or email + password against the API.
   * Throws InvalidCredentials on bad creds, TotpRequired when the account has
   * confirmed 2FA (complete with completeTotpSignIn). Caches the returned
   * user, then loads its prefs.
   */
  async signIn(usernameOrHandle: string, password: string): Promise<string> {
    await csrf();
    let raw: User | { code: 'totp_required'; challenge: string };
    try {
      raw = await api<User | { code: 'totp_required'; challenge: string }>('/auth/login/', {
        method: 'POST',
        body: { username: usernameOrHandle.trim(), password },
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      if (e instanceof ApiError && e.code === 'invalid_credentials') {
        throw new InvalidCredentials();
      }
      if (
        e instanceof ApiError &&
        (e.code === 'account_suspended' || e.code === 'account_banned')
      ) {
        const sanctionStatus = e.code === 'account_suspended' ? 'suspended' : 'banned';
        throw new AccountSanctioned(sanctionStatus, e.reason ?? '', e.category ?? '');
      }
      throw e;
    }
    // 200, not an error: the password was correct but the account needs a
    // second step. The challenge is short-lived server state only.
    if ('code' in raw && raw.code === 'totp_required') {
      throw new TotpRequired(raw.challenge);
    }
    const user = raw as User;
    applySession(user);
    const { prefsService } = await import('./prefsService');
    await prefsService.getPreferences();
    return user.id;
  },

  /**
   * Completes a 2FA sign-in challenge issued by signIn (TotpRequired).
   * `code` may be a 6-digit TOTP code or an unused recovery code. Follows
   * the exact same applySession + prefs flow as signIn's success path.
   */
  async completeTotpSignIn(challenge: string, code: string): Promise<string> {
    let user: User;
    try {
      user = await api<User>('/auth/login/2fa/', {
        method: 'POST',
        body: { challenge, code },
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      if (
        e instanceof ApiError &&
        (e.code === 'account_suspended' || e.code === 'account_banned')
      ) {
        const sanctionStatus = e.code === 'account_suspended' ? 'suspended' : 'banned';
        throw new AccountSanctioned(sanctionStatus, e.reason ?? '', e.category ?? '');
      }
      throw e; // invalid_code / challenge_expired — the caller reads e.code directly
    }
    applySession(user);
    const { prefsService } = await import('./prefsService');
    await prefsService.getPreferences();
    return user.id;
  },

  /**
   * Create a new account via the API. Does NOT auto-login (user must signIn afterwards).
   * Throws UsernameTaken on collision.
   */
  async signUp(fields: SignUpFields): Promise<SignUpResult> {
    await csrf();
    try {
      // The 201 body is the User shape plus ONE top-level key, `invite`,
      // which is only meaningful when a code was sent. `inviteCode` is added
      // to the request body only when present so a plain sign-up's wire is
      // byte-identical to before.
      const raw = await api<User & { invite?: 'applied' | 'invalid' | null }>('/auth/register/', {
        method: 'POST',
        body: {
          handle: fields.handle,
          username: fields.username,
          displayName: fields.displayName ?? fields.fullName,
          email: fields.email,
          password: fields.password,
          ...(fields.inviteCode ? { inviteCode: fields.inviteCode } : {}),
        },
      });
      const invite = raw.invite === 'applied' || raw.invite === 'invalid' ? raw.invite : null;
      // Funnel step (1.24.60, Umami): fires only on a real 201. No-op unless
      // analytics is configured — see core/lib/analytics.ts.
      track('signup-completed');
      return { id: raw.id, invite }; // NO auto-login
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      if (e instanceof ApiError && e.code === 'username_taken') {
        throw new UsernameTaken();
      }
      throw e;
    }
  },

  /** Sign out: best-effort API logout + clear the local session + load guest prefs. */
  signOut(): void {
    void api('/auth/logout/', { method: 'POST' }).catch(() => {});
    authService.endLocalSession();
  },

  /**
   * Forget the signed-in account in THIS browser, without calling the server. signOut
   * uses it after its logout request; the shell uses it alone when the server says the
   * session is already gone (a 401: the cookie expired, the session was ended from
   * another device, or the account was sanctioned).
   */
  endLocalSession(): void {
    clearReplyDrafts();
    setAccounts([]);
    // Everything below is one member's private data held in memory. The next account
    // signed in on this tab must never see it, not even for the moment before its own
    // data loads, and not at all if that load fails.
    useNotificationStore.getState().setNotifications([]);
    useMessageStore.getState().setConversations([]);
    useBookmarkStore.getState().setBookmarks([]);
    useBookmarkStore.getState().setFolders([]);
    useMuteStore.getState().setMutes({});
    useCommunityStore.getState().setInvites([]);
    useHydrationStore.getState().reset();
    // Posts carry per-viewer flags (cherished, spread, poll vote) and the tabs' id
    // lists are per-viewer too.
    useFeedStore.getState().clear();
    usePostStore.getState().setPosts([]);
    // Sign-out hygiene: the next sign-in (same browser, different account) must
    // not inherit a stale follow/request map from whoever was signed in before —
    // useGlobalHydration only re-hydrates from the server, it never clears first.
    useFollowStore.getState().setFollows({});
    useFollowStore.getState().setRequests({});
    // Fix-round item 22: same hygiene for the For You tab's server-picked id
    // list — without this, signing out and back in as a different account
    // could flash the PRIOR account's For You ids for a frame before Home's
    // own effect re-fetches (the list is only ever populated by the explicit
    // ?feed=for-you fetch, so nothing else would clear it first).
    useForYouStore.getState().clear();
    // Same hygiene for the first-run flow's "Remind me later". It is per-TAB
    // session state, so without this one account's dismissal keeps suppressing
    // the welcome flow for the NEXT account signed in on this browser — a brand
    // new member would never see it. (Mobile clears its equivalent flag in the
    // session store's signOut; this is the web counterpart.) The gate module is
    // pure — a key, sessionStorage and a type-only import — so reaching for it
    // from here adds no runtime dependency on the feature layer.
    clearOnboardingDeferral();
    void import('./prefsService')
      .then(({ prefsService }) => prefsService.getPreferences())
      .catch(() => {});
  },

  /**
   * Confirm an email via the signed token from the verification link. Throws ApiError on a
   * bad/expired token (typically code 'invalid_token'). If a session is active, refreshes it
   * so `currentUser.emailVerified` flips live.
   */
  async verifyEmail(token: string): Promise<void> {
    await csrf();
    await api('/auth/email/verify/', { method: 'POST', body: { token } });
    // Funnel step (1.24.60, Umami) — reached only when the token was accepted.
    track('email-verified');
    if (getAccounts().some((a) => a.useThisAsActive === true)) {
      try {
        const user = await api<User>('/auth/me/');
        applySession(user);
      } catch {
        /* ignore — verification still succeeded */
      }
    }
  },

  /** Re-send the verification email for the signed-in account. Requires auth; throws
   * RateLimited when throttled (mirrors signIn/signUp). */
  async resendVerification(): Promise<void> {
    await csrf();
    try {
      await api('/auth/email/resend/', { method: 'POST', body: {} });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      throw e;
    }
  },

  /** Boot-time: hydrate the session from the server cookie. 200 → cache user; else → guest. */
  async loadSession(): Promise<void> {
    try {
      const user = await api<User>('/auth/me/');
      applySession(user);
    } catch {
      setAccounts([]);
    }
  },

  /** Persist profile edits via the API and re-cache the returned user. */
  async updateProfile(fields: ProfileOverrides): Promise<boolean> {
    if (!getAccounts().some((a) => a.useThisAsActive === true)) return false;
    // Stale-response guard. Screens with many independent toggles (Settings →
    // Notifications has 16) can have two PATCHes in flight; the server applies
    // them in arrival order but the RESPONSES can resolve out of order, and an
    // unconditional applySession would let the older full-User payload
    // overwrite the newer one — visibly reverting a switch the server saved.
    // Only the most recently issued request may re-cache the session.
    // Mirrors mobile sessionStore.updateProfile.
    const seq = ++profileUpdateSeq;
    try {
      const user = await api<User>('/users/me/', { method: 'PATCH', body: fields });
      if (seq === profileUpdateSeq) applySession(user);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Contact & recovery (Account Settings). Every request re-authenticates with
   * the password; codes are short-lived. request* returns {token, devCode?} —
   * devCode is present when no real delivery channel exists (phone always, for
   * now; backup email only on the dev mail backend) and the UI displays it.
   */
  async requestPhoneCode(phone: string, password: string): Promise<{ token: string; devCode?: string }> {
    return api<{ token: string; devCode?: string }>('/users/me/phone/', {
      method: 'POST',
      body: { phone, password },
    });
  },

  async confirmPhone(token: string, code: string): Promise<void> {
    const user = await api<User>('/users/me/phone/confirm/', { method: 'POST', body: { token, code } });
    applySession(user);
  },

  async removePhone(password: string): Promise<void> {
    const user = await api<User>('/users/me/phone/', { method: 'DELETE', body: { password } });
    applySession(user);
  },

  async requestBackupEmailCode(email: string, password: string): Promise<{ token: string; devCode?: string }> {
    return api<{ token: string; devCode?: string }>('/users/me/backup-email/', {
      method: 'POST',
      body: { email, password },
    });
  },

  async confirmBackupEmail(token: string, code: string): Promise<void> {
    const user = await api<User>('/users/me/backup-email/confirm/', {
      method: 'POST',
      body: { token, code },
    });
    applySession(user);
  },

  async removeBackupEmail(password: string): Promise<void> {
    const user = await api<User>('/users/me/backup-email/', { method: 'DELETE', body: { password } });
    applySession(user);
  },

  /**
   * TOTP two-factor auth (Settings → Privacy "Session security"). Every call
   * lets ApiError propagate untouched (wrong_password, already_enabled,
   * setup_required, invalid_code, not_enabled — the dialogs read err.code
   * directly, same as the contact-recovery flow above).
   */
  async setupTwoFactor(
    password: string,
  ): Promise<{ otpauthUri: string; qrSvg: string; secret: string; accountHandle?: string }> {
    return api<{ otpauthUri: string; qrSvg: string; secret: string; accountHandle?: string }>(
      '/auth/2fa/setup/',
      {
        method: 'POST',
        body: { password },
      },
    );
  },

  /** Confirms the pending TOTP secret. Re-caches the session (twoFactorEnabled
   * flips live) and returns the 10 recovery codes — shown to the member
   * exactly once, never fetchable again. */
  async confirmTwoFactor(code: string): Promise<string[]> {
    const raw = await api<User & { recoveryCodes: string[] }>('/auth/2fa/confirm/', {
      method: 'POST',
      body: { code },
    });
    const { recoveryCodes, ...user } = raw;
    applySession(user as User);
    return recoveryCodes;
  },

  /** Disables 2FA. If the account was verified, the server unverifies it
   * immediately (badge⇔2FA rule) — re-caching the session reflects that. */
  async disableTwoFactor(password: string, code: string): Promise<void> {
    const user = await api<User>('/auth/2fa/disable/', {
      method: 'POST',
      body: { password, code },
    });
    applySession(user);
  },

  /** Self-only 2FA status read (used to render the Settings row's badge). */
  async getTwoFactorStatus(): Promise<{ enabled: boolean; recoveryCodesRemaining: number }> {
    return api<{ enabled: boolean; recoveryCodesRemaining: number }>('/auth/2fa/');
  },

  /** Session security: end every other session (browsers, devices, mobile app). */
  async signOutOtherSessions(): Promise<number> {
    const r = await api<{ cleared: number }>('/auth/sessions/clear-others/', { method: 'POST' });
    return r.cleared;
  },

  /**
   * Change the active account's password. Requires the CURRENT password — every
   * other account-takeover surface on this product already does, and this one was
   * the exemption.
   *
   * Throws InvalidCredentials when the current password is wrong, so the dialog can
   * say which field is at fault instead of collapsing every failure into "something
   * went wrong". Returns false only for the genuinely unknown cases.
   */
  async updatePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    if (!getAccounts().some((a) => a.useThisAsActive === true)) return false;
    try {
      await api('/auth/password/', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'invalid_password') throw new InvalidCredentials();
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      return false;
    }
  },

  /**
   * Ask for a reset link. Resolves on 200 whether or not the address belongs to an
   * account — the server deliberately answers identically either way, so the UI must
   * NOT try to report "no such account". Throws RateLimited when throttled, and
   * ApiError('mail_failed') when the mail genuinely could not be sent, which is the
   * one case worth telling the member about: this is the only route back into a
   * locked-out account, so silently claiming success would leave them watching an
   * inbox forever.
   */
  async requestPasswordReset(email: string): Promise<void> {
    await csrf();
    try {
      await api('/auth/password/reset/', { method: 'POST', body: { email } });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      throw e;
    }
  },

  /**
   * Complete a reset. Never returns a session: the server sets the password and
   * stops, and the member signs in normally afterwards, so the 2FA gate on
   * /auth/login/ applies exactly as it always does.
   *
   * Throws TotpRequired (with an empty challenge — there is no challenge token in
   * this flow, the reset token itself is the credential) when the account has 2FA
   * and no code was supplied, so the form can reveal a code field and resubmit.
   */
  async confirmPasswordReset(
    token: string,
    newPassword: string,
    code?: string,
  ): Promise<void> {
    await csrf();
    try {
      await api('/auth/password/reset/confirm/', {
        method: 'POST',
        body: { token, newPassword, ...(code ? { code } : {}) },
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'totp_required') throw new TotpRequired('');
      if (e instanceof ApiError && e.code === 'rate_limited') throw new RateLimited();
      throw e;
    }
  },

  /** Return whether the active account is premium. False when signed out. */
  isPremium(): boolean {
    return getAccounts().find((a) => a.useThisAsActive === true)?.premium ?? false;
  },

  /** Whether the active account is platform staff. False when signed out. */
  isAdmin(): boolean {
    const active = getAccounts().find((a) => a.useThisAsActive === true);
    if (!active) return false;
    return getUser(active.userId)?.isAdmin ?? false;
  },

  /** Whether the active account is a superuser (destructive owner actions). False when signed out. */
  isSuperuser(): boolean {
    const active = getAccounts().find((a) => a.useThisAsActive === true);
    if (!active) return false;
    return getUser(active.userId)?.isSuperuser ?? false;
  },

  /**
   * Delete the active account via the API, then clear the local session.
   *
   * The server requires the current password (and cancels a live subscription first).
   * Throws ApiError `wrong_password` (403) or `billing_cancel_failed` (502); in both
   * cases the account still exists and the member stays signed in — the local session
   * is cleared ONLY once the server has really deleted the account.
   */
  async deleteAccount(password: string): Promise<void> {
    if (!getAccounts().some((a) => a.useThisAsActive === true)) return;
    await api('/auth/account/', { method: 'DELETE', body: { password } });
    authService.endLocalSession();
  },

  /** Download the active account's data as JSON from the API. No-op if signed out. */
  async downloadData(): Promise<void> {
    if (!getAccounts().some((a) => a.useThisAsActive === true)) return;
    const data = await api<unknown>('/users/me/download/');
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'truxsocial-export.json';
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Overlay account.profile onto each account's user registry record. Used by tests and
   * offline seeding; NOT in the production boot path (loadSession replaces it).
   */
  hydrateAccountsIntoUsersRegistry(): void {
    for (const acc of getAccounts()) {
      let rec = getUser(acc.userId);
      if (!rec) {
        rec = buildEmptyUserRecord(acc);
        registerUser(rec);
      }
      if (acc.profile) usersService.applyProfileOverrides(rec, acc.profile);
    }
  },
} as const;

// ── Sign in with Google / Apple (1.25.00) ────────────────────────────────────
// The round trip is server-side: the button is a plain navigation to the API's
// start endpoint, which redirects to the provider and, on return, establishes
// the normal session cookie before sending the browser back to `next`. The SPA
// never handles a token. /providers/ says which buttons can honestly be offered.

export type OAuthProvider = 'google' | 'apple';
export type OAuthProviders = Record<OAuthProvider, boolean>;

export const oauthService = {
  /** Which providers this server holds credentials for. A failed lookup means
   *  "none": the buttons then read as not set up, which is true, rather than
   *  as working, which would be a lie that 503s on click. */
  async providers(): Promise<OAuthProviders> {
    try {
      return await api<OAuthProviders>('/auth/oauth/providers/');
    } catch {
      return { google: false, apple: false };
    }
  },
  /** A real href. Same-origin, so the session cookie set on return is ours. */
  startUrl(provider: OAuthProvider, next: string | null): string {
    const q = next ? `?next=${encodeURIComponent(next)}` : '';
    return `/api/auth/oauth/${provider}/start/${q}`;
  },
};
